import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useState } from 'react';
import { Modal as MaterialModal, makeStyles, createStyles, Typography, lighten } from '@material-ui/core';
import { useTheme } from '../../themes';
import { ModalContext } from './Modal.context';
import { CloseIcon } from '../Icon';
import { IconButton } from '../IconButton';
/**
 * enum for each desktopWidthSize type
 */
var desktopWidthSize;
(function (desktopWidthSize) {
    desktopWidthSize[desktopWidthSize["small"] = 450] = "small";
    desktopWidthSize[desktopWidthSize["wide"] = 850] = "wide";
    desktopWidthSize[desktopWidthSize["normal"] = 650] = "normal";
    desktopWidthSize[desktopWidthSize["x-wide"] = 1014] = "x-wide";
})(desktopWidthSize || (desktopWidthSize = {}));
const modalMaxHeightDesktop = '80vh';
/**
 * we're doing a lot of unintuitive things with content padding in this modal due to "variants".
 * encapsulating that logic here for sanity:
 * @param variant - the ModalVariant being used
 * @param breakpointUp - the breakpoint (and up) that we're setting padding for
 * @param theme - theme object from material-ui
 * @param noPaddingDesktop - boolean to remove padding from desktop modal
 *
 * @return the theme-adjusted padding values via `theme.spacing()`
 */
function contentPaddingByVariant(variant, noPaddingDesktop, spacing, breakpointUp) {
    if (noPaddingDesktop) {
        return '0';
    }
    switch (variant) {
        case undefined:
            return spacing(4, 3);
        case 'informational':
            if (breakpointUp === 'sm' || breakpointUp === 'md') {
                return spacing(0, 5, 4);
            }
            return spacing(0, 1);
        case 'nopaddingonmobile':
            if (!breakpointUp || breakpointUp === 'sm') {
                return spacing(0, 0);
            }
            return spacing(4, 3);
        default:
            return spacing(4, 3);
    }
}
/* TODO: remove these functions when c2fo theme is deprecated  */
const getBorder = (palette, themeName, variant) => {
    if (themeName === 'liquidity') {
        return 'none';
    }
    if (variant === 'informational') {
        /*
          This is custom shadow can't remove, will deprecate with c2fo theme
         */
        return `5px solid ${palette.secondary.main}`;
    }
};
const getBorderRadius = (themeName, shape, variant) => {
    if (themeName === 'liquidity') {
        return shape.borderRadius * 4;
    }
    if (variant === 'informational') {
        return shape.borderRadius;
    }
};
const getTextTransform = (themeName, variant) => {
    if (themeName === 'liquidity') {
        return 'capitalize';
    }
    if (variant === 'informational') {
        return 'uppercase';
    }
};
const useStyles = makeStyles(({ palette, spacing, breakpoints, typography, shape, shadows }) => createStyles({
    wrapper: ({ variant, desktopWidth = 'normal', themeName }) => ({
        backgroundColor: palette.background.paper,
        border: getBorder(palette, themeName, variant),
        boxShadow: variant === 'informational' ? shadows[3] : '0',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        outline: 'none',
        position: 'absolute',
        width: '100%',
        [breakpoints.up('sm')]: {
            boxShadow: shadows[20],
            flex: `0 1 ${modalMaxHeightDesktop}`,
            height: 'auto',
            left: '50%',
            maxHeight: modalMaxHeightDesktop,
            maxWidth: desktopWidthSize[desktopWidth],
            top: '10vh',
            transform: 'translate(-50%, 0)',
            width: '90%',
            borderRadius: getBorderRadius(themeName, shape, variant),
        },
    }),
    paper: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 1,
        [breakpoints.up('sm')]: {
            borderRadius: ({ themeName, variant }) => getBorderRadius(themeName, shape, variant),
            maxHeight: modalMaxHeightDesktop,
        },
    },
    titleBar: ({ isOverflow, isScrollAtBottom, isScrollInProgress, variant, themeName }) => ({
        position: 'relative',
        zIndex: 1,
        boxShadow: (isOverflow && isScrollAtBottom) || (isOverflow && isScrollInProgress) ? shadows[10] : undefined,
        alignItems: 'center',
        background: variant === 'informational' ? 'white' : palette.secondary.main,
        color: 'white',
        display: 'flex',
        flex: '1 0 auto',
        justifyContent: 'space-between',
        minHeight: 48,
        padding: spacing(0, 2),
        [breakpoints.up('sm')]: {
            borderTopLeftRadius: getBorderRadius(themeName, shape, variant),
            borderTopRightRadius: getBorderRadius(themeName, shape, variant),
            padding: spacing(0, 3),
        },
    }),
    emptyTitleBar: ({ noPaddingDesktop = false }) => ({
        paddingBottom: noPaddingDesktop ? 0 : spacing(1),
    }),
    title: ({ themeName, variant }) => ({
        color: variant === 'informational' ? palette.secondary.main : 'white',
        textAlign: variant === 'informational' ? 'center' : 'left',
        fontSize: variant === 'informational' ? typography.pxToRem(30) : 'inherit',
        textTransform: getTextTransform(themeName, variant),
        padding: variant === 'informational' ? spacing(5, 0) : '',
        fontWeight: variant === 'informational' ? 'normal' : 'bold',
        width: variant === 'informational' ? '100%' : 'inherit',
    }),
    content: ({ variant, noPaddingDesktop = false }) => ({
        flex: '1 1 100vh',
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: contentPaddingByVariant(variant, noPaddingDesktop, spacing, undefined),
        [breakpoints.up('sm')]: {
            flex: '1 1 auto',
            padding: contentPaddingByVariant(variant, noPaddingDesktop, spacing, 'sm'),
        },
        [breakpoints.up('md')]: {
            padding: contentPaddingByVariant(variant, noPaddingDesktop, spacing, 'md'),
        },
    }),
    backdrop: {
        // This unique color is not part of themes
        backgroundColor: `#aca199 !important`,
        opacity: 0.7,
    },
    closeIcon: ({ variant }) => variant === 'informational'
        ? {
            position: 'absolute',
            top: 8,
            right: 0,
            marginRight: 8,
            color: palette.secondary.main,
        }
        : {},
    actions: ({ isOverflow, isScrollAtTop, isScrollInProgress }) => ({
        position: 'relative',
        zIndex: 20,
        boxShadow: (isOverflow && isScrollAtTop) || (isOverflow && isScrollInProgress) ? shadows[5] : undefined,
        alignItems: 'center',
        borderTop: '1px solid',
        borderTopColor: lighten(palette.text.primary, 0.8),
        display: 'flex',
        flex: '1 0 auto',
        justifyContent: 'flex-end',
        minHeight: 80,
        padding: spacing(0, 2.5),
        width: '100%',
    }),
}));
// eslint-disable-next-line max-statements,complexity
export const Modal = React.forwardRef((props, ref) => {
    const { modalTitle, actionsContent, closeButtonTestId, desktopWidth = 'normal', variant, noPaddingDesktop = false, closeButtonAriaLabel, containerClass } = props, forwardedProps = __rest(props, ["modalTitle", "actionsContent", "closeButtonTestId", "desktopWidth", "variant", "noPaddingDesktop", "closeButtonAriaLabel", "containerClass"]);
    const { name: themeName } = useTheme();
    const { setModalOpen } = useContext(ModalContext);
    const [scrollContainerRef, setScrollContainerRef] = useState(null);
    const setContentRef = (instance) => {
        setScrollContainerRef(instance);
    };
    const maxDesktopWidth = desktopWidthSize[desktopWidth];
    const [resizeValues, setResizeValues] = useState(null);
    const handleScroll = () => {
        var _a, _b, _c;
        const values = {
            offsetHeight: (_a = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.offsetHeight) !== null && _a !== void 0 ? _a : 0,
            scrollHeight: (_b = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.scrollHeight) !== null && _b !== void 0 ? _b : 0,
            scrollTop: (_c = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.scrollTop) !== null && _c !== void 0 ? _c : 0,
        };
        setResizeValues(values);
    };
    /**
     * The following lines containing scroll position logic are uncovered in the code climate test coverage temporarily.
     * Will follow up with Cypress testing once that has been integrated into mono.
     */
    let isOverflow = false;
    let isScrollAtTop = false;
    let isScrollAtBottom = false;
    let isScrollInProgress = false;
    if (resizeValues) {
        if (resizeValues.scrollHeight > resizeValues.offsetHeight && resizeValues.scrollTop === 0) {
            isOverflow = true;
            isScrollAtTop = true;
            isScrollAtBottom = false;
        }
        else if (resizeValues.scrollHeight > resizeValues.offsetHeight &&
            resizeValues.scrollTop + resizeValues.offsetHeight === resizeValues.scrollHeight) {
            isOverflow = true;
            isScrollAtTop = false;
            isScrollAtBottom = true;
        }
        else if (resizeValues.scrollHeight > resizeValues.offsetHeight) {
            isOverflow = true;
            isScrollInProgress = true;
        }
    }
    const classes = useStyles({
        desktopWidth,
        noPaddingDesktop,
        variant,
        isOverflow,
        isScrollAtTop,
        isScrollAtBottom,
        isScrollInProgress,
        themeName,
    });
    /**
     * Handle Modal open/close state
     */
    useEffect(() => {
        setModalOpen(forwardedProps.open);
    }, [setModalOpen, forwardedProps.open]);
    /**
     * Handle scrolling / resizing event listeners
     */
    useEffect(() => {
        if (scrollContainerRef) {
            handleScroll();
            scrollContainerRef.addEventListener('scroll', handleScroll);
            window.addEventListener('resize', handleScroll);
        }
        return () => {
            if (scrollContainerRef) {
                scrollContainerRef.removeEventListener('scroll', handleScroll);
                window.removeEventListener('resize', handleScroll);
            }
        };
    }, [scrollContainerRef]);
    /**
     * We always want to clean up context to open = false on unmount, or
     * we risk not hiding the overlay when this component is unmounted.
     * NOTE: this useEffect should _NOT_ have any dependencies
     * as it's effectively a componentWillUnmount().
     */
    useEffect(() => {
        return () => {
            setModalOpen(false);
        };
    }, []);
    let titleContent;
    if (typeof modalTitle === 'string') {
        titleContent = (_jsx(Typography, Object.assign({ component: "h1", variant: "body1", className: classes.title }, { children: modalTitle }), void 0));
    }
    else {
        titleContent = modalTitle;
    }
    /**
     * If the user doesn't give a title, nor a way to close the modal,
     * then we display an empty div to give the top of the
     * modal some extra padding
     */
    const isEmptyTitleBar = !titleContent && !forwardedProps.onClose;
    return (_jsx(MaterialModal, Object.assign({ BackdropProps: { className: classes.backdrop } }, forwardedProps, { ref: ref }, { children: _jsx("div", Object.assign({ className: classes.wrapper, style: { maxWidth: maxDesktopWidth } }, { children: _jsxs("div", Object.assign({ className: classes.paper }, { children: [isEmptyTitleBar ? (_jsx("div", { className: classes.emptyTitleBar }, void 0)) : (_jsxs("div", Object.assign({ className: classes.titleBar }, { children: [titleContent,
                            forwardedProps.onClose !== undefined && (_jsx(IconButton, Object.assign({ "aria-label": closeButtonAriaLabel || 'Close Modal', "aria-controls": "navigation-more-menu", "aria-haspopup": "true", onClick: (event) => (forwardedProps.onClose ? forwardedProps.onClose(event, 'backdropClick') : null), edge: "end", color: "inherit", className: classes.closeIcon, "data-testid": closeButtonTestId }, { children: _jsx(CloseIcon, {}, void 0) }), void 0))] }), void 0)),
                    _jsx("div", Object.assign({ ref: setContentRef, className: `${classes.content} ${containerClass}` }, { children: forwardedProps.children }), void 0),
                    actionsContent && _jsx("div", Object.assign({ className: classes.actions }, { children: actionsContent }), void 0)] }), void 0) }), void 0) }), void 0));
});
//# sourceMappingURL=Modal.js.map